import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { FileText, ChevronDown, ChevronUp, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { getChildLastActivity } from '../shared/childActivity';
import { useRefreshChild } from '../hooks/useRefreshChild';
import { DataFreshness } from '../../shared/DataFreshness';
import { getAssessmentHistoryForLearner, getReport, type AssessmentReport, type AssessmentHistoryEntry } from '../../../assessment';
import { parseGroupNum } from '../shared/groupUtils';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { Skeleton } from '../../../components/shared/Skeleton';
import { getModuleIcon, getModuleColors } from '../../../content';
interface ToetsenPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  setViewingLearner: (id: string) => void;
}
export function ToetsenPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  setViewingLearner
}: ToetsenPageProps) {
  const {
    refresh
  } = useRefreshChild(selectedChild ? [selectedChild] : []);
  const [history, setHistory] = useState<{
    entry: AssessmentHistoryEntry;
    report: AssessmentReport;
  }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!selectedChild) {
      setHistory([]);
      setIsLoading(false);
      setLoadError(false);
      return;
    }
    setIsLoading(true);
    setLoadError(false);
    const groupNum = parseGroupNum(selectedGroup);
    // AS-2026-07-P1-01: learner scope enforced by the accessor.
    getAssessmentHistoryForLearner(selectedChild.id, groupNum).then(async childHistory => {
      if (cancelled) return;
      const fullHistory = [];
      for (const entry of childHistory) {
        const report = await getReport(entry.attemptId);
        if (report) {
          fullHistory.push({
            entry,
            report
          });
        }
      }
      if (!cancelled) {
        setHistory(fullHistory);
        setIsLoading(false);
      }
    }).catch(() => {
      // AS-2026-07-P3-02: surface the failure instead of rendering the
      // "nog geen toetsen gemaakt" empty state over a broken read.
      if (!cancelled) {
        setHistory([]);
        setLoadError(true);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedChild?.id, selectedGroup]);
  if (!selectedChild) {
    return (
      // Zie VoortgangPage: "Geen data beschikbaar" leest als een storing,
      // terwijl er alleen nog geen kind gekoppeld is.
      <PageLayout title="Toetsen" description="Bekijk de toetshistorie" data-id="element-8908" data-mp="1wogtxrr1mydl">
        <div className="text-center py-16 max-w-md mx-auto" data-id="element-8909" data-mp="1hz2m4o348p0b">
          <p className="text-slate-700 font-semibold mb-1" data-id="element-8910" data-mp="1mw1fv5ivatxz">
            Nog geen kind gekoppeld
          </p>
          <p className="text-sm text-slate-500" data-id="element-8911" data-mp="2alvr5injgag4">
            Koppel eerst een kind bij Instellingen. Daarna zie je hier de
            toetsen die het heeft gemaakt.
          </p>
        </div>
      </PageLayout>
    );
  }
  const latestReport = history.length > 0 ? history[0].report : null;
  return <PageLayout title="Toetsen" description={`Bekijk de toetshistorie van ${selectedChild.displayName}`} actions={<DataFreshness updatedAt={getChildLastActivity(childData)} onRefresh={refresh} data-id="element-8913" data-mp="q58wnz0n0lrz" />} data-id="element-8912" data-mp="25masl9ca643x">
      
      <div className="max-w-4xl mx-auto space-y-8 pb-12" data-id="element-8914" data-mp="u8yjaixr4tad">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8915" data-mp="1af1pngbslh2x" />
        

        {isLoading ? <div className="space-y-4" data-id="element-8916" data-mp="15na63pr20bma">
            <Skeleton className="h-40 w-full rounded-2xl" data-id="element-8917" data-mp="15m8o11bmeov2" />
            <Skeleton className="h-20 w-full rounded-xl" data-id="element-8918" data-mp="10q9z9cmdr4u4" />
            <Skeleton className="h-20 w-full rounded-xl" data-id="element-8919" data-mp="vq5hlj9qk42b" />
          </div> : loadError ? <Card className="flex flex-col items-center justify-center p-12 text-center border-2 border-amber-200 bg-amber-50/60" role="alert" data-id="element-8920" data-mp="vo2nan1jvors">
          
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-amber-100 mb-5" data-id="element-8921" data-mp="1ofumi7pkgo97">
              <AlertCircle className="w-8 h-8 text-amber-600" data-id="element-8922" data-mp="1jdwzd2e1p9az" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8923" data-mp="1fdldjm9y52xx">
              Toetshistorie kon niet geladen worden
            </h3>
            <p className="text-slate-600 max-w-md mb-5" data-id="element-8924" data-mp="7y8c09wvq89n">
              Er is niets verwijderd — we konden de gegevens van{' '}
              {selectedChild.displayName} even niet ophalen.
            </p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-white border border-amber-300 text-amber-900 rounded-lg text-sm font-bold hover:bg-amber-50 transition-colors" data-id="element-8925" data-mp="2jnwllfo3pykf">
            
              Opnieuw proberen
            </button>
          </Card> : history.length === 0 ? <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed border-2 border-slate-200 bg-slate-50/50" data-id="element-8926" data-mp="2tfkell0b3no0">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 mb-6" data-id="element-8927" data-mp="33aukijttsf72">
              <DjayNarrator size="lg" emotion="thinking" data-id="element-8928" data-mp="37eahjh9t4hka" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8929" data-mp="3cbanls6npkbe">
              Nog geen toetsen gemaakt
            </h3>
            <p className="text-slate-500 max-w-md" data-id="element-8930" data-mp="1d1kkwrs20u97">
              Zodra {selectedChild.displayName} een niveaubepaling of toets
              afrondt, verschijnt hier de historie en het geschatte CITO-niveau.
            </p>
          </Card> : <div className="space-y-8" data-id="element-8931" data-mp="2ganbndqid61e">
            {/* Hero: Latest Assessment */}
            {latestReport && <section data-id="element-8932" data-mp="2u9wt0gqga66y">
                <h2 className="text-lg font-black text-slate-900 mb-4" data-id="element-8933" data-mp="2ub7ta6kosvtb">
                  Laatste Resultaat
                </h2>
                <Card className="p-6 sm:p-8 border-emerald-200 bg-emerald-50/50 shadow-sm" data-id="element-8934" data-mp="2p9qtkedxvfeb">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-8935" data-mp="346rosrvc9shj">
                    <div className="flex items-center gap-5" data-id="element-8936" data-mp="2z8q2ly2m9d1u">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white border border-emerald-100 shadow-sm text-emerald-600" data-id="element-8937" data-mp="3d0w8mrc223nk">
                        <FileText className="w-8 h-8" data-id="element-8938" data-mp="3mytxnh1qo4h8" />
                      </div>
                      <div data-id="element-8939" data-mp="3mwpresgdatds">
                        <div className="flex items-center gap-2 mb-1" data-id="element-8940" data-mp="3hzzci8gau3ty">
                          <span className="text-sm font-bold text-emerald-800 uppercase tracking-wider" data-id="element-8941" data-mp="3hxwf33cth8f7">
                            Niveaubepaling
                          </span>
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md" data-id="element-8942" data-mp="aegdrdme9fny">
                            Groep {latestReport.selectedGroup}
                          </span>
                        </div>
                        <div className="text-2xl font-black text-slate-900" data-id="element-8943" data-mp="3ivb4xvttxbm7">
                          {new Date(latestReport.completedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 self-start sm:self-center bg-white p-4 rounded-2xl border border-emerald-100 shadow-sm" data-id="element-8944" data-mp="2zw5q7cfp3ia2">
                      <div className="text-right" data-id="element-8945" data-mp="33wh9aksetajf">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-8946" data-mp="3kd02m1nrvgqa">
                          Geschat Niveau
                        </div>
                        <div className="text-3xl font-black text-slate-800 leading-none" data-id="element-8947" data-mp="3fcccdi7db3x4">
                          {latestReport.overallEstimatedLevelCode}
                        </div>
                      </div>

                      {(() => {
                  const cito = levelCodeToCitoLevel(latestReport.overallEstimatedLevelCode, latestReport.selectedGroup);
                  if (!cito) return null;
                  return <div className={`flex items-center justify-center px-4 py-2 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8948" data-mp="2rom3tjov0gdo">
                        
                            <span className={`text-xl font-black ${cito.color}`} data-id="element-8949" data-mp="2mrcfy8h4ccry">
                          
                              {cito.label}
                            </span>
                          </div>;
                })()}
                    </div>
                  </div>
                </Card>
              </section>}

            {/* History List */}
            <section data-id="element-8950" data-mp="x7ckyy0ku514">
              <h2 className="text-lg font-black text-slate-900 mb-4" data-id="element-8951" data-mp="x6t3fieu9inm">
                Historie
              </h2>
              <div className="space-y-3" data-id="element-8952" data-mp="125e3nhu3c2c2">
                {history.map(({
              entry,
              report
            }, index) => {
              const isExpanded = expandedId === entry.attemptId;
              const prevReport = history[index + 1]?.report;
              const cito = levelCodeToCitoLevel(report.overallEstimatedLevelCode, report.selectedGroup);
              const panelId = `toets-panel-${entry.attemptId}`;
              return <Card key={entry.attemptId} className={`overflow-hidden transition-all duration-200 border-slate-200 ${isExpanded ? 'ring-2 ring-emerald-500 border-transparent shadow-md' : 'hover:border-emerald-300 hover:shadow-sm'}`} data-id="element-8953" data-mp="o87orychmns4" data-mpi={index}>
                    
                      <div role="button" tabIndex={0} aria-expanded={isExpanded} aria-controls={panelId} onClick={() => setExpandedId(isExpanded ? null : entry.attemptId)} onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : entry.attemptId);
                  }
                }} className="p-4 sm:p-5 flex items-center justify-between gap-4 bg-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500" data-id="element-8954" data-mp="3rupxo9u6neg7">
                      
                        <div className="flex items-center gap-4" data-id="element-8955" data-mp="255bset56le6w">
                          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400" data-id="element-8956" data-mp="2a5hmefyxbcs5">
                            <FileText className="w-5 h-5" data-id="element-8957" data-mp="1hhva8jbmqzto" />
                          </div>
                          <div data-id="element-8958" data-mp="1hg0zfxu2oo3b">
                            <div className="font-bold text-slate-900" data-id="element-8959" data-mp="1mgevehieg7kz">
                              {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-8960" data-mp="3qop01468bylr">
                              Groep {entry.selectedGroup} •{' '}
                              {entry.totalQuestions} vragen
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 sm:gap-6" data-id="element-8961" data-mp="3c02m96w93ewn">
                          <div className="text-right hidden sm:block" data-id="element-8962" data-mp="3813jnsxzru8d">
                            <div className="text-lg font-black text-slate-800" data-id="element-8963" data-mp="3317f9sldu0sa">
                              {report.overallEstimatedLevelCode}
                            </div>
                          </div>

                          {cito && <div className={`w-8 h-8 rounded-lg flex items-center justify-center border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8964" data-mp="2t2hv1t60k48v">
                          
                              <span className={`text-sm font-black ${cito.color}`} data-id="element-8965" data-mp="2odyxg8xkix7c">
                            
                                {cito.label}
                              </span>
                            </div>}

                          <div className="text-slate-400" data-id="element-8966" data-mp="2efav0ii2gqor">
                            {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-8967" data-mp="325x1i5cfa2wn" /> : <ChevronDown className="w-5 h-5" data-id="element-8968" data-mp="326ybs7lm6ldb" />}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Sub-scores */}
                      <AnimatePresence initial={false} data-id="element-8969" data-mp="2tcjxo8ctlgvs">
                        {isExpanded && <motion.div id={panelId} key="panel" initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="border-t border-slate-100 bg-slate-50/50 overflow-hidden" data-id="element-8970" data-mp="2xeew953d9g5v">
                        
                            <div className="p-4 sm:p-5" data-id="element-8971" data-mp="1o5aabi60irio">
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8972" data-mp="1j6pcfpe8gjxe">
                                Scores per vak
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-8973" data-mp="qitkexzlzg6y">
                                {report.estimatedLevels.map(mod => {
                          const IconComp = getModuleIcon(mod.moduleId);
                          const colors = getModuleColors(mod.moduleId);
                          const prevMod = prevReport?.estimatedLevels.find(m => m.moduleId === mod.moduleId);
                          const delta = prevMod ? mod.estimatedTier - prevMod.estimatedTier : 0;
                          return <div key={mod.moduleId} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-8974" data-mp="lfauuce0bfm2">
                                  
                                      <div className="flex items-center gap-3" data-id="element-8975" data-mp="uexscn3m819u">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg} ${colors.text}`} data-id="element-8976" data-mp="zetnrxy8ykds">
                                      
                                          <IconComp className="w-4 h-4" data-id="element-8977" data-mp="19chwxswlnufh" />
                                        </div>
                                        <div className="font-bold text-slate-700 text-sm" data-id="element-8978" data-mp="1e2oifjvteyti">
                                          {mod.moduleTitle}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3" data-id="element-8979" data-mp="1n5fw7qc5y6ob">
                                        {prevMod && <div className="flex items-center text-xs font-bold" data-id="element-8980" data-mp="1s3r7m5i3wp8n">
                                            {delta > 0 ? <span className="text-emerald-500 flex items-center" aria-label="Gestegen sinds vorige toets" title="Gestegen sinds vorige toets" data-id="element-8981" data-mp="228h9kbdiftcv">
                                        
                                                <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-8982" data-mp="1sat7t1hhlll7" />
                                              </span> : delta < 0 ? <span className="text-rose-500 flex items-center" aria-label="Gedaald sinds vorige toets" title="Gedaald sinds vorige toets" data-id="element-8983" data-mp="1o9hkx3pzzegp">
                                        
                                                <TrendingDown className="w-3 h-3 mr-0.5" data-id="element-8984" data-mp="2b7cihv7xvhjv" />
                                              </span> : <span className="text-slate-300 flex items-center" aria-label="Gelijk gebleven" title="Gelijk gebleven" data-id="element-8985" data-mp="2769297lzbji4">
                                        
                                                <Minus className="w-3 h-3 mr-0.5" data-id="element-8986" data-mp="2yx6vqdw6784n" />
                                              </span>}
                                          </div>}
                                        <div className="text-sm font-black text-slate-900 bg-slate-100 px-2 py-1 rounded-md" data-id="element-8987" data-mp="x1pszbqzr0lx">
                                          {mod.estimatedLevelCode}
                                        </div>
                                      </div>
                                    </div>;
                        })}
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </Card>;
            })}
              </div>
            </section>

            {/* Disclaimer */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-8988" data-mp="4dbvcx3te6aw">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-8989" data-mp="9bvi2zq8fm4b" />
              <div className="text-sm text-blue-800 leading-relaxed" data-id="element-8990" data-mp="1ymw05zlk0dmk">
                <strong data-id="element-8991" data-mp="23ms7a7pjsl6c">Let op:</strong> De getoonde niveaus zijn een
                inschatting op basis van de gemaakte opgaven in Meester Djay.
                Dit is geen officiële CITO-score en kan afwijken van de
                resultaten op school. Gebruik deze gegevens als indicatie van de
                groei.
              </div>
            </div>
          </div>}
      </div>
    </PageLayout>;
}